import feeService from 'ApiService/Accountant/feeService';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { PaymentModal } from './PaymentModal';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import { AuthContext } from 'contexts/AuthContext';

const StudentFeeDetails = ({ students, classData, loadingStudent }) => {
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedInstallmentId, setSelectedInstallmentId] = useState(null);
  const [selectedFeeType, setSelectedFeeType] = useState(null);
  const [selectedSwapId, setSelectedSwapId] = useState(null);
  const [selectedPaymentInfo, setSelectedPaymentInfo] = useState(null);
  const [isModalOpenConfirmation, setIsModalOpenConfirmation] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amountPaid: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentType: '',
    txnNumber: '',
    course: '',
    batch: '',
  });
  const [errors, setErrors] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const { state } = useContext(AuthContext);
  const navigate = useNavigate();

  const fetchStudentDetails = async (studentId) => {
    setIsLoadingDetails(true);
    try {
      const response = await feeService.getStudentFeeDetails(studentId);
      if (response.data.success) {
        setSelectedStudent(response.data.data);
      } else {
        toast.error('Failed to fetch student details.');
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Error fetching student details.'
      );
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleStudentSelect = (e) => {
    const studentId = e.target.value;
    if (!studentId) {
      setSelectedStudent(null);
      return;
    }
    fetchStudentDetails(studentId);
  };

  const handlePaymentChange = (e) => {
    setPaymentData({ ...paymentData, [e.target.name]: e.target.value });
  };

  const handleInstallmentSelect = (installment, feeType, swapId = null) => {
    const dueDate = new Date(installment.dueDate).toISOString().split('T')[0];

    let courseInfo, batchInfo;

    if (feeType === 'original') {
      courseInfo = selectedStudent.feeBreakdown.original.course;
      batchInfo = selectedStudent.feeBreakdown.original.batch;
    } else if (feeType === 'swap') {
      const swapInfo = selectedStudent.feeBreakdown.swaps.find(
        (swap) => swap.swapId === swapId
      );
      courseInfo = swapInfo.swappedToCourse;
      batchInfo = swapInfo.swappedToBatch;
    }

    setSelectedPaymentInfo({
      courseName: courseInfo.name,
      batchNumber: batchInfo.batchNumber,
      courseId: courseInfo.id,
      batchId: batchInfo.id,
    });

    setPaymentData({
      amountPaid: installment.amount.toString(),
      paymentDate: dueDate,
      paymentType: '',
      txnNumber: '',
      course: courseInfo.id,
      batch: batchInfo.id,
    });

    setSelectedInstallmentId(installment._id);
    setSelectedFeeType(feeType);
    setSelectedSwapId(swapId);
    setIsModalOpen(true);
  };

  const handleFullPaymentSelect = (feeData, feeType, swapId = null) => {
    let courseInfo, batchInfo;

    if (feeType === 'original') {
      courseInfo = selectedStudent.feeBreakdown.original.course;
      batchInfo = selectedStudent.feeBreakdown.original.batch;
    } else if (feeType === 'swap') {
      const swapInfo = selectedStudent.feeBreakdown.swaps.find(
        (swap) => swap.swapId === swapId
      );
      courseInfo = swapInfo.swappedToCourse;
      batchInfo = swapInfo.swappedToBatch;
    }

    setSelectedPaymentInfo({
      courseName: courseInfo.name,
      batchNumber: batchInfo.batchNumber,
      courseId: courseInfo.id,
      batchId: batchInfo.id,
    });

    setPaymentData({
      amountPaid: feeData.pendingAmount.toString(),
      paymentDate: new Date().toISOString().split('T')[0],
      paymentType: '',
      txnNumber: '',
      course: courseInfo.id,
      batch: batchInfo.id,
    });

    setSelectedInstallmentId(null);
    setSelectedFeeType(feeType);
    setSelectedSwapId(swapId);
    setIsModalOpen(true);
  };

  const Confirmation = async () => {
    setIsModalOpenConfirmation(true);
  };

  const handlePayment = async () => {
    try {
      if (!selectedStudent) {
        toast.error('Please select a student first');
        return;
      }

      const totalRemainingFee =
        selectedStudent.feeBreakdown.overall.totalPendingAmount;
      const amountPaid = parseFloat(paymentData.amountPaid);

      if (amountPaid <= 0) {
        toast.error('Payment amount must be greater than zero');
        return;
      }

      if (amountPaid > totalRemainingFee) {
        toast.error(
          `Payment cannot exceed remaining fee of ₹${totalRemainingFee.toLocaleString()}`
        );
        return;
      }

      const response = await feeService.makePayment({
        formData: {
          studentId: selectedStudent.student.id,
          course: paymentData.course,
          batch: paymentData.batch,
          ...paymentData,
          amountPaid: parseFloat(paymentData.amountPaid),
          installmentId: selectedInstallmentId,
          selectedFeeType: selectedFeeType,
        },
      });

      if (response.data.success) {
        toast.success('Payment successful');
        fetchStudentDetails(selectedStudent.student.id);
        setPaymentData({
          amountPaid: '',
          paymentDate: new Date().toISOString().split('T')[0],
          paymentType: '',
          txnNumber: '',
          course: '',
          batch: '',
        });
        setIsModalOpenConfirmation(false);
        setSelectedPaymentInfo(null);
        setIsModalOpen(false);
      } else {
        toast.error(response.data.message || 'Payment failed');
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          'An error occurred while processing the payment'
      );
    }
  };

  const renderInstallments = (installments, feeType, swapId = null) => {
    return installments.map((installment, index) => (
      <div
        key={`${feeType}-${swapId}-${index}`}
        onClick={() =>
          !installment.isPaid &&
          handleInstallmentSelect(installment, feeType, swapId)
        }
        className={`rounded-lg border ${
          installment.isPaid
            ? 'border-green-100 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
            : 'border-gray-100 bg-white dark:border-white/5 dark:bg-navy-800'
        } p-4 shadow-sm transition-all ${
          !installment.isPaid
            ? 'cursor-pointer hover:border-brand-500 hover:shadow-md dark:hover:border-brand-400'
            : ''
        }`}
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Due Date
          </span>
          <span
            className={`text-sm ${
              new Date(installment.dueDate) < new Date() && !installment.isPaid
                ? 'text-red-500'
                : installment.isPaid
                ? 'text-green-600 dark:text-green-400'
                : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            {formatDate(installment.dueDate)}
          </span>
        </div>

        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Amount
          </span>
          <span
            className={`text-sm font-semibold ${
              installment.isPaid
                ? 'text-green-600 dark:text-green-400'
                : 'text-navy-700 dark:text-white'
            }`}
          >
            ₹{installment.amount.toLocaleString()}
          </span>
        </div>

        {installment.afterLoanDeduction > 0 && (
          <div className="mb-2 flex items-center justify-between text-sm font-semibold">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Amount After Deduction
            </span>
            <span>₹{installment.afterLoanDeduction.toLocaleString()}</span>
          </div>
        )}

        {installment.isEmiInstallment && (
          <div className="mb-2 text-xs font-semibold text-blue-500 dark:text-blue-400">
            EMI Installment
          </div>
        )}

        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Status
          </span>
          <span
            className={`text-sm font-medium ${
              installment.isPaid ? 'text-green-500' : 'text-red-500'
            }`}
          >
            {installment.isPaid ? 'Paid' : 'Pending'}
          </span>
        </div>

        {!installment.isPaid && (
          <div className="mt-2 text-xs text-brand-500 dark:text-brand-400">
            Click to make payment
          </div>
        )}
      </div>
    ));
  };

  const renderPaymentOptions = (feeData, feeType, swapId = null) => {
    if (feeData.isFullPayment) {
      return (
        <div
          onClick={() => handleFullPaymentSelect(feeData, feeType, swapId)}
          className={`cursor-pointer rounded-lg border p-4 shadow-sm transition-all ${
            feeData.pendingAmount === 0
              ? 'border-green-100 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
              : 'border-gray-100 bg-white hover:border-brand-500 hover:shadow-md dark:border-white/5 dark:bg-navy-800 dark:hover:border-brand-400'
          }`}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Full Payment
            </span>
            <span className="text-sm font-semibold text-navy-700 dark:text-white">
              ₹{feeData.totalAmount.toLocaleString()}
            </span>
          </div>

          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Status
            </span>
            <span
              className={`text-sm font-medium ${
                feeData.pendingAmount === 0 ? 'text-green-500' : 'text-red-500'
              }`}
            >
              {feeData.pendingAmount === 0 ? 'Paid' : 'Pending'}
            </span>
          </div>

          {feeData.pendingAmount > 0 && (
            <div className="mt-2 text-xs text-brand-500 dark:text-brand-400">
              Click to make payment
            </div>
          )}
        </div>
      );
    }

    if (feeData.installments && feeData.installments.length > 0) {
      return renderInstallments(feeData.installments, feeType, swapId);
    }

    if (feeData.pendingAmount > 0) {
      return (
        <div
          onClick={() => handleFullPaymentSelect(feeData, feeType, swapId)}
          className="cursor-pointer rounded-lg border border-gray-100 bg-white p-4 shadow-sm transition-all hover:border-brand-500 hover:shadow-md dark:border-white/5 dark:bg-navy-800 dark:hover:border-brand-400"
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Lump Sum Payment
            </span>
            <span className="text-sm font-semibold text-navy-700 dark:text-white">
              ₹{feeData.pendingAmount.toLocaleString()}
            </span>
          </div>

          <div className="mt-2 text-xs text-brand-500 dark:text-brand-400">
            Click to make payment
          </div>
        </div>
      );
    }

    return null;
  };

  useEffect(() => {
    const newErrors = [];
    if (
      !paymentData.paymentDate ||
      !paymentData.paymentType ||
      !paymentData.txnNumber
    ) {
      newErrors.push('Please fill in all fields.');
    }
    setErrors(newErrors);
  }, [paymentData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="w-full rounded-lg bg-white pt-6 dark:bg-navy-800">
      <h4 className="mb-6 text-xl font-bold text-navy-700 dark:text-white">
        Student Fee Details
      </h4>

      {/* Student Selection */}
      <div className="mb-6">
        <select
          onChange={handleStudentSelect}
          className="w-full rounded-xl border border-gray-200 p-3 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
        >
          <option value="">Select a Student</option>
          {loadingStudent ? (
            <option>Loading students...</option>
          ) : (
            students.map((student) => (
              <option key={student._id} value={student._id}>
                {student.studentName}
              </option>
            ))
          )}
        </select>
      </div>

      {isLoadingDetails && (
        <div className="flex justify-center py-4">
          <p className="text-navy-700 dark:text-white">
            Loading student details...
          </p>
        </div>
      )}

      {selectedStudent && (
        <div className="space-y-6">
          {/* Fee Overview Card */}
          <div className="grid gap-4 rounded-xl bg-gray-50 p-4 dark:bg-navy-900 md:grid-cols-3">
            <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
              <h6 className="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-400">
                Total Fee
              </h6>
              <p className="text-2xl font-bold text-navy-700 dark:text-white">
                ₹
                {selectedStudent.feeBreakdown.overall.totalAmount.toLocaleString()}
              </p>
            </div>

            <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
              <h6 className="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-400">
                Paid Fee
              </h6>
              <p className="text-2xl font-bold text-green-500">
                ₹
                {selectedStudent.feeBreakdown.overall.totalPaidAmount.toLocaleString()}
              </p>
            </div>

            <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
              <h6 className="mb-2 text-sm font-semibold text-gray-600 dark:text-gray-400">
                Remaining Fee
              </h6>
              <p className="text-2xl font-bold text-red-500">
                ₹
                {selectedStudent.feeBreakdown.overall.totalPendingAmount.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Fee Payment Forms */}
          <div className="rounded-xl border border-gray-200 p-4 dark:border-white/10">
            {/* Original Fee Section */}
            <div className="mb-6 rounded-lg bg-yellow-50 p-4 dark:bg-navy-900">
              <div className="flex flex-col space-y-2">
                <div className="flex items-center justify-between">
                  <h5 className="font-semibold text-yellow-700 dark:text-yellow-400">
                    Original Fee Details
                  </h5>
                  <span className="text-sm font-semibold text-yellow-700 dark:text-yellow-400">
                    Pending: ₹
                    {selectedStudent.feeBreakdown.original.pendingAmount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm text-yellow-600 dark:text-yellow-300">
                  <span>
                    Total Fee: ₹
                    {selectedStudent.feeBreakdown.original.totalAmount.toLocaleString()}
                  </span>
                  <span>
                    Paid: ₹
                    {selectedStudent.feeBreakdown.original.paidAmount.toLocaleString()}
                  </span>
                </div>
                <div className="text-sm text-yellow-600 dark:text-yellow-300">
                  Course: {selectedStudent.feeBreakdown.original.course.name}{' '}
                  (Batch{' '}
                  {selectedStudent.feeBreakdown.original.batch.batchNumber})
                </div>
              </div>
            </div>

            {/* Original Fee Payment Options */}
            <div className="mb-6">
              <h5 className="mb-4 font-semibold text-navy-700 dark:text-white">
                {selectedStudent?.feeBreakdown?.original.isFullPayment ||
                selectedStudent?.feeBreakdown?.original.installments?.length ===
                  0
                  ? 'Original Fee (Full Payment)'
                  : 'Original Fee Installments'}
              </h5>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {renderPaymentOptions(
                  selectedStudent.feeBreakdown.original,
                  'original'
                )}
              </div>
            </div>

            {/* Swap Sections */}
            {selectedStudent.feeBreakdown.swaps.map((swap, index) => (
              <div key={swap.swapId} className="mb-6">
                <div className="mb-4 rounded-lg bg-blue-50 p-4 dark:bg-navy-900">
                  <h5 className="mb-2 font-semibold text-blue-700 dark:text-blue-400">
                    Course Swap Fee #{index + 1}
                  </h5>
                  <div className="grid gap-2 text-sm text-blue-600 dark:text-blue-300">
                    <div>
                      From: {swap.swappedFromCourse.name} (Batch{' '}
                      {swap.swappedFromBatch.batchNumber})
                    </div>
                    <div>
                      To: {swap.swappedToCourse.name} (Batch{' '}
                      {swap.swappedToBatch.batchNumber})
                    </div>
                    <div className="flex justify-between">
                      <span>Total: ₹{swap.totalAmount.toLocaleString()}</span>
                      <span>Paid: ₹{swap.paidAmount.toLocaleString()}</span>
                      <span>
                        Pending: ₹{swap.pendingAmount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h5 className="mb-4 font-semibold text-navy-700 dark:text-white">
                    {swap.isFullPayment
                      ? 'Swap Fee (Full Payment)'
                      : 'Swap Fee Installments'}
                  </h5>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {renderPaymentOptions(swap, 'swap', swap.swapId)}
                  </div>
                </div>
              </div>
            ))}

            {/* Payment Modal */}
            <PaymentModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              paymentInfo={{
                selectedPaymentInfo,
                paymentData,
                handlePaymentChange,
              }}
              onSubmit={Confirmation}
              errors={errors}
            />

            {/* Complete Payment Message */}
            {selectedStudent.feeBreakdown.overall.isFullyPaid && (
              <div className="flex items-center justify-center rounded-lg bg-green-50 p-6 dark:bg-navy-900">
                <div className="text-center">
                  <h5 className="font-semibold text-green-700 dark:text-green-400">
                    All payments have been completed
                  </h5>
                  <p className="mt-2 text-sm text-green-600 dark:text-green-300">
                    Total Paid: ₹
                    {selectedStudent.feeBreakdown.overall.totalPaidAmount.toLocaleString()}
                  </p>
                </div>
              </div>
            )}

            {/* Add Installment Button Section */}

            {(() => {
              const checkIfNeedsInstallments = (feeComponent) => {
                if (feeComponent.isFullPayment === true) {
                  return 0;
                }

                if (feeComponent.pendingAmount <= 0) {
                  return 0;
                }

                const existingInstallmentsTotal = (
                  feeComponent.installments || []
                )
                  .filter((installment) => !installment.isPaid)
                  .reduce(
                    (total, installment) => total + installment.amount,
                    0
                  );

                return Math.max(
                  0,
                  feeComponent.pendingAmount - existingInstallmentsTotal
                );
              };

              const originalNeedsInstallments = checkIfNeedsInstallments(
                selectedStudent.feeBreakdown.original
              );

              const swapsNeedingInstallments =
                selectedStudent.feeBreakdown.swaps.reduce(
                  (total, swap) => total + checkIfNeedsInstallments(swap),
                  0
                );

              const totalAmountNeedingInstallments =
                originalNeedsInstallments + swapsNeedingInstallments;

              const getInstallmentRoute = () => {
                const userRole = state?.user?.role;

                switch (userRole.toLowerCase()) {
                  case 'superadmin':
                    return '/superadmin/update/installment/dates';
                  case 'admin':
                    return '/admin/update/installment/dates';
                  case 'account':
                  default:
                    return '/accountant/update/installment/dates';
                }
              };

              return (
                totalAmountNeedingInstallments > 0 && (
                  <div className="rounded-xl border border-yellow-300 bg-yellow-50 p-4 dark:border-gray-800 dark:bg-navy-700">
                    <h5 className="mb-2 text-lg font-semibold text-yellow-700 dark:text-white/60">
                      Note:
                    </h5>
                    <p className="mb-4 text-sm text-yellow-600 dark:text-white/60">
                      This student has a pending fee balance of ₹
                      {totalAmountNeedingInstallments.toLocaleString()} that
                      requires installments. Please add installment(s) for this
                      amount.
                    </p>
                    <button
                      onClick={() => {
                        const route = getInstallmentRoute();
                        navigate(route);
                      }}
                      className="rounded-xl bg-yellow-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600"
                    >
                      Add Installment
                    </button>
                  </div>
                )
              );
            })()}
            {/* {selectedStudent.feeBreakdown.overall.totalPendingAmount > 0 && (
              <div className="rounded-xl border border-yellow-300 bg-yellow-50 p-4 dark:border-gray-800 dark:bg-navy-700">
                <h5 className="mb-2 text-lg font-semibold text-yellow-700 dark:text-white/60">
                  Note:
                </h5>
                <p className="mb-4 text-sm text-yellow-600 dark:text-white/60">
                  This student has a pending fee balance of ₹
                  {selectedStudent.feeBreakdown.overall.totalPendingAmount.toLocaleString()}
                  . Please add a new installment for the outstanding amount.
                </p>
                <button
                  onClick={() => {
                    navigate('/accountant/update/installment/dates');
                  }}
                  className="rounded-xl bg-yellow-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600"
                >
                  Add Installment
                </button>
              </div>
            )} */}
          </div>
        </div>
      )}
      <ConfirmationModal
        isOpen={isModalOpenConfirmation}
        onClose={() => setIsModalOpenConfirmation(false)}
        onConfirm={handlePayment}
        title="Confirm Payment"
        message={
          <div className="space-y-3 text-left">
            <p className="text-lg font-semibold text-navy-700 dark:text-white">
              Please confirm the following payment details:
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg bg-gray-50 p-3 dark:bg-navy-900">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Student:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                {selectedStudent?.student?.name || 'Selected Student'}
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Course:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                {selectedPaymentInfo?.courseName || ''}
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Batch:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                {selectedPaymentInfo?.batchNumber || ''}
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Amount:
              </span>
              <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                ₹{parseFloat(paymentData.amountPaid).toLocaleString()}
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Payment Date:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                {formatDate(paymentData.paymentDate)}
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Payment Type:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                {paymentData.paymentType}
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Transaction #:
              </span>
              <span className="text-sm text-navy-700 dark:text-white">
                {paymentData.txnNumber}
              </span>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Fee Type:
              </span>
              <span className="text-sm capitalize text-navy-700 dark:text-white">
                {selectedFeeType === 'original'
                  ? 'Original Course Fee'
                  : 'Course Swap Fee'}
              </span>
            </div>
            <p className="mt-2 text-sm text-red-500">
              This action cannot be undone. Please verify all details before
              confirming.
            </p>
          </div>
        }
        confirmText="Confirm Payment"
        cancelText="Cancel"
        icon="payment"
      />
    </div>
  );
};

export default StudentFeeDetails;
